import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { Avatar, Box, Button, Card, Chip, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import BookingCreatedAtLabel from '../BookingCreatedAtLabel';
import PassBookingChip from '../passes/PassBookingChip';
import { PaymentMethod } from '../../models/Organization';
import { PaymentStatus, RoomBooking, StripeRefundStatus } from '../../models/RoomBooking';
import { tutorAvatarInitials } from '../../utils/tutorAvatarInitials';
import { getTutorRefundStatusChipLabel } from '../../utils/tutorBookingPayment';

function bookingListImageUrl(booking: RoomBooking) {
  return booking.spaceImageUrl?.trim() || booking.roomImageUrl?.trim() || undefined;
}

function SeriesThumbnail({ booking, size }: { booking: RoomBooking; size: number }) {
  return (
    <Avatar
      src={bookingListImageUrl(booking)}
      alt=""
      variant="rounded"
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        bgcolor: 'action.hover',
        color: 'text.secondary',
        fontSize: size >= 44 ? '0.875rem' : '0.75rem',
        fontWeight: 600,
      }}
    >
      {tutorAvatarInitials(booking.spaceName)}
    </Avatar>
  );
}

function PaymentMethodLabel({ method }: { method?: PaymentMethod | null }) {
  const { t } = useTranslation();
  const normalized = method?.toString().toUpperCase();
  const icon =
    normalized === PaymentMethod.CASH ? (
      <AttachMoneyIcon fontSize="small" />
    ) : normalized === PaymentMethod.ONLINE ? (
      <CreditCardIcon fontSize="small" />
    ) : null;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
      {icon}
      <Typography variant="body2" color="text.secondary" fontSize="0.75rem" sx={{ whiteSpace: 'nowrap' }}>
        {method
          ? t(`paymentMethod.${method.toLowerCase()}`)
          : t('paymentMethod.unknown')}
      </Typography>
    </Box>
  );
}

type SeriesCardProps = {
  bookings: RoomBooking[];
  onOpen: (bookingId: string) => void;
  onCancel: () => void;
  formatWhen: (startTime: string, endTime: string) => string;
  formatAmount: (amount: number, currencyCode: string) => string;
};

function seriesTotal(bookings: RoomBooking[]) {
  return bookings.reduce((sum, booking) => sum + booking.totalAmount, 0);
}

function earliestCreatedAt(bookings: RoomBooking[]) {
  return bookings
    .map((booking) => booking.createdAt?.trim())
    .filter((value): value is string => Boolean(value))
    .sort()[0];
}

function paymentStatusColor(status: string | null | undefined) {
  switch (status) {
    case PaymentStatus.PAID:
      return 'success' as const;
    case PaymentStatus.AMOUNT_BLOCKED:
      return 'warning' as const;
    case PaymentStatus.BLOCK_REQUESTED:
      return 'info' as const;
    case PaymentStatus.CANCELLED:
      return 'error' as const;
    default:
      return 'default' as const;
  }
}

function refundStatusColor(status: StripeRefundStatus) {
  switch (status) {
    case StripeRefundStatus.SUCCEEDED:
      return 'success' as const;
    case StripeRefundStatus.PENDING:
      return 'warning' as const;
    case StripeRefundStatus.FAILED:
    case StripeRefundStatus.CANCELED:
      return 'error' as const;
    case StripeRefundStatus.REQUIRES_ACTION:
      return 'info' as const;
    default:
      return 'default' as const;
  }
}

function SeriesPaymentStatus({ bookings }: { bookings: RoomBooking[] }) {
  const { t } = useTranslation();
  const statuses = Array.from(
    new Set(
      bookings
        .filter((booking) => !booking.pass && !booking.stripeRefundStatus)
        .map((booking) => booking.paymentStatus)
        .filter((status): status is PaymentStatus => Boolean(status)),
    ),
  );
  const refunds = Array.from(
    new Set(
      bookings
        .map((booking) => booking.stripeRefundStatus)
        .filter((status): status is StripeRefundStatus => Boolean(status)),
    ),
  );
  const passes = bookings.flatMap((booking) => (booking.pass ? [booking.pass] : []));

  if (statuses.length === 0 && refunds.length === 0 && passes.length === 0) {
    return null;
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {statuses.map((status) => (
        <Chip
          key={status}
          size="small"
          label={t(`paymentStatus.${status.toLowerCase()}`)}
          color={paymentStatusColor(status)}
        />
      ))}
      {refunds.map((status) => (
        <Chip
          key={status}
          size="small"
          icon={<ReceiptIcon fontSize="small" />}
          label={getTutorRefundStatusChipLabel(status, t)}
          color={refundStatusColor(status)}
        />
      ))}
      {passes.map((pass) => (
        <PassBookingChip key={pass.passId} pass={pass} />
      ))}
    </Stack>
  );
}

function SessionList({
  bookings,
  onOpen,
  formatWhen,
  nowrap = false,
}: Pick<SeriesCardProps, 'bookings' | 'onOpen' | 'formatWhen'> & { nowrap?: boolean }) {
  return (
    <Stack spacing={0} alignItems="flex-start">
      {bookings.map((booking) => (
        <Typography
          key={booking.roomBookingId}
          component="button"
          type="button"
          variant="body2"
          onClick={() => onOpen(booking.roomBookingId)}
          sx={{
            p: 0,
            border: 0,
            bgcolor: 'transparent',
            color: 'inherit',
            font: 'inherit',
            lineHeight: 1.3,
            textAlign: 'left',
            whiteSpace: nowrap ? 'nowrap' : 'normal',
            cursor: 'pointer',
            '&:hover': {
              color: 'primary.main',
              textDecoration: 'underline',
            },
          }}
        >
          {formatWhen(booking.startTime, booking.endTime)}
        </Typography>
      ))}
    </Stack>
  );
}

export function UserRoomSeriesMobileCard({
  bookings,
  onOpen,
  onCancel,
  formatWhen,
  formatAmount,
}: SeriesCardProps) {
  const { t } = useTranslation();
  const rep = bookings[0];
  const total = seriesTotal(bookings);

  return (
    <Card
      elevation={2}
      sx={{
        p: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Stack spacing={1.5}>
        <Chip
          label={t(`status.${rep.status.toLowerCase()}`, rep.status)}
          color="warning"
          size="medium"
          sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
        />
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <SeriesThumbnail booking={rep} size={44} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {rep.spaceName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {rep.roomName}
            </Typography>
          </Box>
        </Stack>
        <Stack spacing={0.75}>
          <SessionList bookings={bookings} onOpen={onOpen} formatWhen={formatWhen} />
          <BookingCreatedAtLabel
            createdAt={earliestCreatedAt(bookings)}
            timezone={rep.timezone}
            placement="footer"
          />
        </Stack>
        <Stack spacing={0.25} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            {t('bookings.totalForSessions', {
              count: bookings.length,
              defaultValue: 'Total for {{count}} sessions',
            })}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" color="primary" fontWeight={700}>
              {formatAmount(total, rep.currencyCode)}
            </Typography>
            {!rep.pass ? <PaymentMethodLabel method={rep.paymentMethod} /> : null}
          </Stack>
          <SeriesPaymentStatus bookings={bookings} />
        </Stack>
        <Button variant="outlined" color="error" onClick={onCancel}>
          {t('bookings.cancelAllSessions', 'Cancel all')}
        </Button>
      </Stack>
    </Card>
  );
}

export function UserRoomSeriesTableRow({
  bookings,
  onOpen,
  onCancel,
  formatWhen,
  formatAmount,
  showDivider,
}: SeriesCardProps & { showDivider: boolean }) {
  const { t } = useTranslation();
  const rep = bookings[0];
  const total = seriesTotal(bookings);

  return (
    <>
      <TableRow
        sx={{
          '& td': {
            borderBottom: 'none',
            verticalAlign: 'middle',
            py: 1,
          },
        }}
      >
        <TableCell>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <SeriesThumbnail booking={rep} size={36} />
            <Typography variant="body2">{rep.spaceName}</Typography>
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{rep.roomName}</Typography>
        </TableCell>
        <TableCell>
          <Stack spacing={0.25}>
            <SessionList bookings={bookings} onOpen={onOpen} formatWhen={formatWhen} nowrap />
            <BookingCreatedAtLabel
              createdAt={earliestCreatedAt(bookings)}
              timezone={rep.timezone}
              placement="footer"
            />
          </Stack>
        </TableCell>
        <TableCell>
          <Stack spacing={0.25}>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
              {t('bookings.totalForSessions', {
                count: bookings.length,
                defaultValue: 'Total for {{count}} sessions',
              })}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'nowrap',
                width: 'max-content',
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                {formatAmount(total, rep.currencyCode)}
              </Typography>
              {!rep.pass ? <PaymentMethodLabel method={rep.paymentMethod} /> : null}
            </Box>
            <SeriesPaymentStatus bookings={bookings} />
          </Stack>
        </TableCell>
        <TableCell>
          <Chip
            label={t(`status.${rep.status.toLowerCase()}`, rep.status)}
            color="warning"
            size="small"
          />
        </TableCell>
        <TableCell>
          <Button variant="outlined" color="error" size="small" onClick={onCancel}>
            {t('bookings.cancelAllSessions', 'Cancel all')}
          </Button>
        </TableCell>
      </TableRow>
      {showDivider && (
        <TableRow>
            <TableCell
            colSpan={6}
            sx={{
              height: 8,
              p: 0,
              borderTop: '1px solid',
              borderBottom: 'none',
              borderColor: 'divider',
              bgcolor: 'transparent',
            }}
          />
        </TableRow>
      )}
    </>
  );
}
